"use client";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Gift } from "lucide-react";

const GiftShopBanner = () => {
  const router = useRouter();
  const [sparkles, setSparkles] = useState<Array<{ left: number; emoji: string }>>([]);

  useEffect(() => {
    const emojis = ["🎁", "✨", "🎀", "💝", "⭐", "🎉"];
    const sparkleArray = Array.from({ length: 40 }, () => ({
      left: Math.random() * 100,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
    }));
    setSparkles(sparkleArray);
  }, []);

  const banner: React.CSSProperties = {
    width: "100%",
    minHeight: "650px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2C2C2C",
    textAlign: "center",
    background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
    marginBottom: "80px",
  };

  const decorCircle1: React.CSSProperties = {
    position: "absolute",
    top: "-150px",
    right: "-200px",
    width: "500px",
    height: "500px",
    background: "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  };

  const decorCircle2: React.CSSProperties = {
    position: "absolute",
    bottom: "-100px",
    left: "-150px",
    width: "400px",
    height: "400px",
    background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  };

  const textContainer: React.CSSProperties = {
    position: "relative",
    zIndex: 2,
    maxWidth: "900px",
    padding: "0 20px",
    animation: "fadeSlide 1.2s ease",
  };

  const giftIconWrapper: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "90px",
    height: "90px",
    borderRadius: "24px",
    background: "rgba(255,255,255,0.25)",
    backdropFilter: "blur(10px)",
    border: "3px solid rgba(255,255,255,0.4)",
    marginBottom: "28px",
    boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
    animation: "float 3s ease-in-out infinite",
  };

  const badge: React.CSSProperties = {
    display: "inline-block",
    padding: "12px 28px",
    background: "rgba(255,255,255,0.25)",
    backdropFilter: "blur(10px)",
    color: "#fff",
    borderRadius: "50px",
    fontSize: "14px",
    fontWeight: 800,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    marginBottom: "24px",
    border: "2px solid rgba(255,255,255,0.4)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
  };

  const title: React.CSSProperties = {
    fontSize: "5rem",
    fontWeight: 900,
    marginBottom: "24px",
    lineHeight: 1.1,
    color: "#fff",
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: "-2px",
    textShadow: "0 4px 30px rgba(0,0,0,0.2)",
  };

  const subtitle: React.CSSProperties = {
    fontSize: "1.6rem",
    fontWeight: 600,
    marginBottom: "40px",
    color: "rgba(255,255,255,0.95)",
    fontFamily: "'Montserrat', sans-serif",
    lineHeight: 1.6,
    textShadow: "0 2px 20px rgba(0,0,0,0.15)",
  };

  const button: React.CSSProperties = {
    padding: "20px 55px",
    fontSize: "1.15rem",
    fontWeight: 800,
    color: "#FF6B6B",
    background: "#fff",
    border: "3px solid rgba(255,255,255,0.5)",
    borderRadius: "50px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    letterSpacing: "1px",
    textTransform: "uppercase",
    position: "relative",
    overflow: "hidden",
  };

  const sparkleStyle = (left: number): React.CSSProperties => ({
    position: "absolute",
    top: "-20px",
    left: `${left}%`,
    fontSize: "20px",
    opacity: 0.3,
    animation: `sparkleFall ${6 + Math.random() * 5}s linear infinite`,
    animationDelay: `${Math.random() * 4}s`,
    zIndex: 1,
  });

  const keyframes = `
    @keyframes fadeSlide {
      0% { opacity: 0; transform: translateY(30px); }
      100% { opacity: 1; transform: translateY(0); }
    }

    @keyframes sparkleFall {
      0% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
      50% { opacity: 0.6; }
      100% { transform: translateY(650px) rotate(360deg); opacity: 0; }
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-15px); }
    }

    .gift-button:hover {
      transform: translateY(-3px) scale(1.05);
      box-shadow: 0 15px 40px rgba(0,0,0,0.25);
      background: #fff;
      color: #2C2C2C;
    }

    .gift-button::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border-radius: 50%;
      background: rgba(255,255,255,0.4);
      transform: translate(-50%, -50%);
      transition: width 0.6s ease, height 0.6s ease;
    }

    .gift-button:hover::before {
      width: 350px;
      height: 350px;
    }

    /* MOBILE RESPONSIVE */
    @media (max-width: 768px) {
      .banner-section {
        min-height: 550px !important;
        margin-bottom: 60px !important;
      }

      .gift-icon-wrapper {
        width: 70px !important;
        height: 70px !important;
        margin-bottom: 20px !important;
        border-radius: 20px !important;
      }

      .gift-icon-wrapper svg {
        width: 36px !important;
        height: 36px !important;
      }

      .banner-badge {
        font-size: 12px !important;
        padding: 10px 22px !important;
        margin-bottom: 20px !important;
      }

      .banner-title {
        font-size: 3rem !important;
        margin-bottom: 20px !important;
        letter-spacing: -1px !important;
      }

      .banner-subtitle {
        font-size: 1.2rem !important;
        margin-bottom: 32px !important;
      }

      .banner-button {
        padding: 16px 45px !important;
        font-size: 1rem !important;
      }

      .decor-circle-1, .decor-circle-2 {
        width: 300px !important;
        height: 300px !important;
      }
    }

    @media (max-width: 480px) {
      .banner-section {
        min-height: 500px !important;
        margin-bottom: 50px !important;
      }

      .gift-icon-wrapper {
        width: 60px !important;
        height: 60px !important;
        border-radius: 16px !important;
      }

      .gift-icon-wrapper svg {
        width: 32px !important;
        height: 32px !important;
      }

      .banner-badge {
        font-size: 11px !important;
        padding: 8px 20px !important;
        letter-spacing: 1px !important;
      }

      .banner-title {
        font-size: 2.3rem !important;
        letter-spacing: -0.5px !important;
      }

      .banner-subtitle {
        font-size: 1.05rem !important;
      }

      .banner-button {
        padding: 14px 38px !important;
        font-size: 0.95rem !important;
      }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>

      <div className="banner-section" style={banner}>
        {/* Decorative Circles */}
        <div className="decor-circle-1" style={decorCircle1}></div>
        <div className="decor-circle-2" style={decorCircle2}></div>

        {/* Sparkles */}
        {sparkles.map((sparkle, index) => (
          <div key={index} style={sparkleStyle(sparkle.left)}>
            {sparkle.emoji}
          </div>
        ))}

        {/* Content */}
        <div style={textContainer}>
          <div className="gift-icon-wrapper" style={giftIconWrapper}>
            <Gift style={{ width: "40px", height: "40px", color: "#fff" }} />
          </div>

          <div className="banner-badge" style={badge}>
            🎉 New Arrivals
          </div>

          <h1 className="banner-title" style={title}>
            Perfect Gifts for Every Occasion
          </h1>

          <p className="banner-subtitle" style={subtitle}>
            Discover thoughtfully curated gifts that bring smiles and create lasting memories
          </p>

          <button
            className="gift-button banner-button"
            style={button}
            onClick={() => router.push("/shop")}
          >
            <span style={{ position: "relative", zIndex: 1 }}>
              🎁 Explore Gifts
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default GiftShopBanner;